/**
 * 페이지마다 다른 순서의 "다른 장례식장" 리스트를 보여주기 위한 유틸.
 *
 * SEO 문제: 같은 시도 내 모든 페이지가 동일한 추천 리스트를 보여주면
 *   네이버가 페이지간 차별성을 인식 못 해 흡수(url_mismatch) 시킴.
 *
 * 해법:
 *   - 시군구 단위로 필터 (currentParlor.sigungu) — 페이지 위치별 자연스러운 다양성
 *   - currentParlorId 시드로 deterministic shuffle — 같은 시군구 내에서도 페이지마다 순서 다름
 */

/**
 * 시드 기반 deterministic Fisher-Yates shuffle.
 *
 * - 같은 seed → 같은 결과 (SSR 안전)
 * - 다른 seed → 다른 결과 (페이지마다 순서 다름)
 *
 * LCG (Linear Congruential Generator)로 의사난수 생성. 분포 품질은 SEO 다양성 목적엔 충분.
 */
export function deterministicShuffle<T>(arr: T[], seed: number): T[] {
  const result = [...arr];
  let s = (seed >>> 0) || 1; // 0 회피 (LCG가 0에서 멈춤)
  for (let i = result.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) >>> 0; // Numerical Recipes LCG
    const j = s % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * 두 지점의 거리(km). Haversine formula.
 *
 * 지구 반경 6371km. 정확도 1m 수준 (충분).
 */
export function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * 거리 표시용 포맷.
 *   - 1km 미만 → "350m"
 *   - 1~10km → "5.2km"
 *   - 10km 이상 → "12km"
 */
export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  if (km < 10) return `${km.toFixed(1)}km`;
  return `${Math.round(km)}km`;
}

/**
 * 시군구 우선 필터, 결과가 너무 적으면 시도 전체로 fallback.
 *
 * @param parlors 전체 장례식장 데이터
 * @param sido 시도 코드
 * @param sigungu 시군구 코드 (없으면 시도 전체)
 * @param minSize 시군구 결과가 이 수 미만이면 시도로 fallback
 */
export function applySigunguFallback<T extends { sido: string; sigungu: string }>(
  parlors: T[],
  sido: string | undefined,
  sigungu: string | undefined,
  minSize: number
): T[] {
  if (sigungu) {
    const sigunguFiltered = parlors.filter(
      (p) => String(p.sigungu) === String(sigungu)
    );
    if (sigunguFiltered.length >= minSize) {
      return sigunguFiltered;
    }
    // 시군구 결과가 적으면 시도로 fallback
  }
  if (sido) {
    return parlors.filter((p) => String(p.sido) === String(sido));
  }
  return parlors;
}
